import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useAssistantContext } from "~/components/assistant/AssistantContext";
import { BlobFileMessage } from "~/components/assistant/messages/BlobFileMessage";
import { CancellationMessage } from "~/components/assistant/messages/CancellationMessage";
import { DataValueMessage } from "~/components/assistant/messages/DataValueMessage";
import { DataframeMessage } from "~/components/assistant/messages/DataframeMessage";
import { FigureMessage } from "~/components/assistant/messages/FigureMessage";
import { FilterMessage } from "~/components/assistant/messages/FilterMessage";
import { TextFileMessage } from "~/components/assistant/messages/TextFileMessage";
import { ToolMessage } from "~/components/assistant/messages/ToolMessage";
import type { MessagePart } from "~/components/assistant/types";
import { MARKDOWN_COMPONENTS } from "./markdownComponents";

// Markdown component wrapper
function Markdown({ children }: { children: string }) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
      {children}
    </ReactMarkdown>
  );
}

// Default renderer components
function ContentRenderer({ part }: { part: MessagePart; debugMode?: boolean }) {
  if (!part.content) {
    return null;
  }

  return (
    <div className="px-3 py-1">
      <Markdown>{part.content}</Markdown>
    </div>
  );
}

function CancelledRenderer() {
  return (
    <div className="px-3 py-1">
      <CancellationMessage />
    </div>
  );
}

function FigureRenderer({ part, debugMode }: { part: MessagePart; debugMode?: boolean }) {
  if (!part.figure) {
    return null;
  }
  return (
    <div className="px-2">
      <FigureMessage figure={part.figure} debugMode={debugMode || false} />
    </div>
  );
}

function DataframeRenderer({ part, debugMode }: { part: MessagePart; debugMode?: boolean }) {
  if (!part.dataframe) {
    return null;
  }
  return (
    <div className="px-2">
      <DataframeMessage dataframe={part.dataframe} debugMode={debugMode || false} />
    </div>
  );
}

function ValueRenderer({ part, debugMode }: { part: MessagePart; debugMode?: boolean }) {
  if (!part.value) {
    return null;
  }
  return (
    <div className="px-2">
      <DataValueMessage value={part.value} debugMode={debugMode || false} />
    </div>
  );
}

function ToolCallRenderer({ part }: { part: MessagePart; debugMode?: boolean }) {
  if (!part.toolCall) {
    return null;
  }

  return (
    <div className="px-2">
      <ToolMessage toolCall={part.toolCall} expanded={false} />
    </div>
  );
}

function TextFileRenderer({ part, debugMode }: { part: MessagePart; debugMode?: boolean }) {
  if (!part.textFile) {
    return null;
  }
  return (
    <div className="px-2">
      <TextFileMessage textFile={part.textFile} debugMode={debugMode || false} />
    </div>
  );
}

function BlobFileRenderer({ part, debugMode }: { part: MessagePart; debugMode?: boolean }) {
  if (!part.blobFile) {
    return null;
  }
  return (
    <div className="px-2">
      <BlobFileMessage blobFile={part.blobFile} debugMode={debugMode || false} />
    </div>
  );
}

function FiltersRenderer({ part }: { part: MessagePart }) {
  const { appContext } = useAssistantContext();
  if (!part.filters || appContext === "builder-agent") {
    return null;
  }
  return (
    <div className="px-2">
      <FilterMessage filters={part.filters} />
    </div>
  );
}

// Export default renderers for registration
export const defaultMessageTypeRenderers = [
  {
    type: "content",
    component: ContentRenderer,
  },
  {
    type: "figure",
    component: FigureRenderer,
  },
  {
    type: "dataframe",
    component: DataframeRenderer,
  },
  {
    type: "value",
    component: ValueRenderer,
  },
  {
    type: "text_file",
    component: TextFileRenderer,
  },
  {
    type: "blob_file",
    component: BlobFileRenderer,
  },
  {
    type: "toolCall",
    component: ToolCallRenderer,
  },
  {
    type: "cancelled",
    component: CancelledRenderer,
  },
  {
    type: "filters",
    component: FiltersRenderer,
  },
];
